import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BarChart3Icon,
  CheckIcon,
  ChevronsUpDownIcon,
  CircleHelpIcon,
  CreditCardIcon,
  KeyRoundIcon,
  Loader2Icon,
  MessageSquareIcon,
  SearchIcon,
  UserRoundIcon,
  UsersIcon,
  XIcon,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { AuthLayout } from '@/layouts/AuthLayout';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import type { SupportCategory, SupportRequestDto } from '@/lib/types';
import { cn } from '@/lib/utils';

const CATEGORY_VALUES = ['sign_in', 'account', 'contacts', 'messaging', 'reporting', 'billing', 'other'] as const;

/** Mirrors CATEGORY_LABELS in the API's supportService. `keywords` widen the search. */
export const SUPPORT_CATEGORIES: Array<{ value: SupportCategory; label: string; hint: string; keywords: string; icon: LucideIcon }> = [
  { value: 'sign_in', label: 'Signing in', hint: 'Password, one-time codes, locked out', keywords: 'login password code locked reset 2fa mfa', icon: KeyRoundIcon },
  { value: 'account', label: 'My account', hint: 'Name, email, mobile number, job access', keywords: 'profile email phone role permission access invite', icon: UserRoundIcon },
  { value: 'contacts', label: 'Contacts and imports', hint: 'Uploading a list, wrong numbers, tags', keywords: 'import csv excel spreadsheet upload numbers tags opt out join', icon: UsersIcon },
  { value: 'messaging', label: 'Sending messages', hint: 'Compose, audiences, something did not send', keywords: 'compose send text sms translation keyword schedule weather automation', icon: MessageSquareIcon },
  { value: 'reporting', label: 'Reports and delivery', hint: 'Delivery status, history, exports', keywords: 'delivered failed history export report status', icon: BarChart3Icon },
  { value: 'billing', label: 'Billing and pilot', hint: 'Plan, pilot dates, invoices', keywords: 'invoice plan pilot price payment', icon: CreditCardIcon },
  { value: 'other', label: 'Something else', hint: 'Anything that does not fit above', keywords: 'other feature request bug idea', icon: CircleHelpIcon },
];

const schema = z.object({
  /** Every topic that applies; the first one routes the request (the API records a single category). */
  categories: z.array(z.enum(CATEGORY_VALUES)).min(1, 'Pick at least one topic'),
  subject: z.string().trim().min(3, 'Give your request a short title').max(160),
  message: z.string().trim().min(10, 'Tell us a little more, at least 10 characters').max(4000),
  name: z.string().trim().min(1, 'Enter your name').max(100),
  email: z.email('Enter a valid email address'),
  phone: z.string().trim().max(40).optional(),
});
type Values = z.infer<typeof schema>;

const STEPS = [
  { key: 'topic', title: 'What is this about?', description: 'Search for the closest topics so the request reaches the right person. Pick every one that applies.', fields: ['categories'] as const },
  { key: 'details', title: 'Tell us what happened', description: 'A short title and what you were trying to do. The more specific, the faster the fix.', fields: ['subject', 'message'] as const },
  { key: 'contact', title: 'How should we reach you?', description: 'We reply by email. Add a mobile number only if you would like a call back.', fields: ['name', 'email', 'phone'] as const },
];

/**
 * Public Help page: a three-step request form (topics, details, contact) in the wide sign-in shell
 * without the product demo, so the form has room. Reachable before and after sign-in; when the
 * visitor is signed in the contact step is pre-filled and the request is linked to their account.
 * Several topics may be chosen: the first becomes the request's category and the others are noted
 * at the top of the message.
 */
export function HelpPage() {
  const { branding } = useBranding();
  const { status, me } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from;
  const signedIn = status === 'authenticated';
  const [step, setStep] = useState(0);
  const [done, setDone] = useState<SupportRequestDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      categories: [],
      subject: '',
      message: '',
      name: me?.user.fullName ?? '',
      email: me?.user.email ?? '',
      phone: me?.user.phone ?? '',
    },
  });
  const { errors, isSubmitting } = form.formState;
  const current = STEPS[step] as (typeof STEPS)[number];
  const isLast = step === STEPS.length - 1;

  const next = async () => {
    const ok = await form.trigger([...current.fields]);
    if (ok) setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const pageUrl = from ? `${window.location.origin}${from}` : document.referrer || undefined;
      const [primary, ...rest] = values.categories as [SupportCategory, ...SupportCategory[]];
      const extra = rest.map((v) => SUPPORT_CATEGORIES.find((c) => c.value === v)?.label ?? v);
      const message = extra.length > 0 ? `Also about: ${extra.join(', ')}.\n\n${values.message}` : values.message;
      const res = await api.support.create({
        name: values.name,
        email: values.email.trim(),
        phone: values.phone?.trim() || undefined,
        category: primary,
        subject: values.subject,
        message,
        pageUrl,
      });
      setDone(res.request);
    } catch (err) {
      setError(errorMessage(err, 'Could not send your request'));
    }
  });

  const backLink = signedIn ? (
    <>
      <Link to="/jobs" className="hover:text-foreground underline underline-offset-4">
        Back to your jobs
      </Link>
      {' · '}
      <Link to="/support" className="hover:text-foreground underline underline-offset-4">
        Your requests
      </Link>
    </>
  ) : (
    <Link to="/login" className="hover:text-foreground underline underline-offset-4">
      Back to sign in
    </Link>
  );

  if (done) {
    return (
      <AuthLayout
        demo={false}
        eyebrow="Request received"
        title="We're on it"
        description={
          <>
            Your request is logged as <span className="text-foreground font-medium">{done.reference}</span>. We sent a confirmation to{' '}
            <span className="text-foreground font-medium">{done.requester.email}</span>, and the {branding.tenantName} team will reply there.
          </>
        }
        footer={backLink}
        tenantLine={
          <>
            Quote <span className="text-foreground font-medium">{done.reference}</span> if you follow up by email.
          </>
        }
      >
        <div className="grid gap-5">
          <div className="border-primary bg-muted/40 border-l-2 px-5 py-4">
            <p className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">Your reference</p>
            <p className="font-display mt-1 text-3xl tracking-tight">{done.reference}</p>
          </div>
          <dl className="grid gap-2 text-sm">
            <div className="flex gap-3">
              <dt className="text-muted-foreground w-16 shrink-0">Topic</dt>
              <dd>{done.categoryLabel}</dd>
            </div>
            <div className="flex gap-3">
              <dt className="text-muted-foreground w-16 shrink-0">Subject</dt>
              <dd>{done.subject}</dd>
            </div>
          </dl>
          <Button asChild variant="outline" size="lg" className="w-full">
            <Link to={signedIn ? '/jobs' : '/login'}>{signedIn ? 'Back to your jobs' : 'Back to sign in'}</Link>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const chosen = SUPPORT_CATEGORIES.filter((c) => form.watch('categories').includes(c.value));
  const PrimaryIcon = chosen[0]?.icon;

  return (
    <AuthLayout
      demo={false}
      wide
      eyebrow={`Help · Step ${step + 1} of ${STEPS.length}`}
      title={current.title}
      description={current.description}
      footer={
        <span>
          {backLink}
          {branding.supportEmail && (
            <>
              {' '}
              · Prefer email?{' '}
              <a className="hover:text-foreground underline underline-offset-4" href={`mailto:${branding.supportEmail}`}>
                {branding.supportEmail}
              </a>
            </>
          )}
        </span>
      }
      tenantLine={
        <>
          Your request goes to the <span className="text-foreground font-medium">{branding.tenantName}</span> team.
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (isLast) void onSubmit(e);
          else void next();
        }}
        className="grid gap-6"
        noValidate
      >
        <Progress step={step} />
        <FormError message={error} />

        {step === 0 && (
          <Controller
            control={form.control}
            name="categories"
            render={({ field }) => (
              <Field label="Topics" htmlFor="topics" description="The first topic routes your request; the others are noted for the team." error={errors.categories?.message}>
                <TopicPicker id="topics" value={field.value} onChange={field.onChange} onDone={() => void next()} invalid={!!errors.categories} />
              </Field>
            )}
          />
        )}

        {step === 1 && (
          <>
            <Field label="Subject" htmlFor="subject" error={errors.subject?.message}>
              <Input id="subject" autoFocus placeholder="Short summary, e.g. Reset link never arrived" aria-invalid={!!errors.subject} {...form.register('subject')} />
            </Field>
            <Field label="What happened?" htmlFor="message" description="Include the job name and roughly when it happened if you can." error={errors.message?.message}>
              <Textarea id="message" rows={6} placeholder="Describe what you were trying to do and what you saw instead." aria-invalid={!!errors.message} {...form.register('message')} />
            </Field>
          </>
        )}

        {step === 2 && (
          <>
            {chosen.length > 0 && (
              <div className="bg-muted/40 flex items-start gap-3 border px-3.5 py-3 text-sm">
                {PrimaryIcon ? <PrimaryIcon className="text-primary mt-0.5 size-4 shrink-0" aria-hidden /> : null}
                <div className="min-w-0">
                  <div className="font-medium">{form.watch('subject')}</div>
                  <div className="text-muted-foreground text-xs">{chosen.map((c) => c.label).join(' · ')}</div>
                </div>
              </div>
            )}
            <div className="grid gap-6 sm:grid-cols-2">
              <Field label="Your name" htmlFor="name" error={errors.name?.message}>
                <Input id="name" autoComplete="name" autoFocus={!signedIn} placeholder="Terrance Cole" aria-invalid={!!errors.name} {...form.register('name')} />
              </Field>
              <Field label="Work email" htmlFor="email" error={errors.email?.message}>
                <Input id="email" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck={false} placeholder="you@company.com" aria-invalid={!!errors.email} {...form.register('email')} />
              </Field>
            </div>
            <Field label="Mobile number" htmlFor="phone" description="Optional." error={errors.phone?.message}>
              <Input id="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="(407) 555-0100" aria-invalid={!!errors.phone} {...form.register('phone')} />
            </Field>
          </>
        )}

        <div className={cn('flex items-center gap-3', step > 0 ? 'justify-between' : 'justify-end')}>
          {step > 0 && (
            <Button type="button" variant="ghost" onClick={back} disabled={isSubmitting} className="h-11 px-3">
              <ArrowLeftIcon aria-hidden /> Back
            </Button>
          )}
          <Button type="submit" size="lg" className="group h-11 min-w-[160px]" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2Icon className="animate-spin" aria-hidden /> Sending…
              </>
            ) : isLast ? (
              <>
                Send request
                <ArrowRightIcon className="transition-transform group-hover:translate-x-0.5" aria-hidden />
              </>
            ) : (
              <>
                Continue
                <ArrowRightIcon className="transition-transform group-hover:translate-x-0.5" aria-hidden />
              </>
            )}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}

/**
 * Searchable multi-select of topics: a field-shaped trigger that shows the chosen topics as chips,
 * opening a command palette with type-to-filter. Chips can be removed in place; the first chip is the
 * primary topic and can be reordered simply by removing and re-adding.
 */
function TopicPicker({ id, value, onChange, onDone, invalid }: { id: string; value: SupportCategory[]; onChange: (v: SupportCategory[]) => void; onDone: () => void; invalid?: boolean }) {
  const [open, setOpen] = useState(false);
  const chosen = value.map((v) => SUPPORT_CATEGORIES.find((c) => c.value === v)).filter((c): c is (typeof SUPPORT_CATEGORIES)[number] => !!c);

  const toggle = (v: SupportCategory) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={invalid || undefined}
          className={cn(
            'border-input bg-background flex min-h-11 w-full items-center gap-2 border px-3 py-1.5 text-left text-sm transition-[color,box-shadow] outline-none',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
          )}
        >
          <SearchIcon className="text-muted-foreground size-4 shrink-0" aria-hidden />
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {chosen.length === 0 ? (
              <span className="text-muted-foreground">Search topics, e.g. “import”, “password”, “delivery”…</span>
            ) : (
              chosen.map((c, i) => (
                <span key={c.value} className={cn('inline-flex h-7 items-center gap-1.5 border pr-1 pl-2 text-xs', i === 0 ? 'border-primary bg-primary text-primary-foreground' : 'bg-muted')}>
                  <c.icon className="size-3.5" aria-hidden />
                  {c.label}
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${c.label}`}
                    className={cn('flex size-5 items-center justify-center transition-colors', i === 0 ? 'hover:bg-primary-foreground/20' : 'hover:bg-foreground/10')}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(c.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        toggle(c.value);
                      }
                    }}
                  >
                    <XIcon className="size-3" aria-hidden />
                  </span>
                </span>
              ))
            )}
          </span>
          <ChevronsUpDownIcon className="text-muted-foreground size-4 shrink-0" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <Command loop>
          <CommandInput autoFocus placeholder="Type to filter topics…" />
          <CommandList className="max-h-[320px]">
            <CommandEmpty>No topic matches. Try “Something else”.</CommandEmpty>
            <CommandGroup heading={chosen.length > 0 ? `${chosen.length} selected · press Enter to toggle` : 'Pick every topic that applies'}>
              {SUPPORT_CATEGORIES.map((c) => {
                const active = value.includes(c.value);
                return (
                  <CommandItem key={c.value} value={`${c.label} ${c.hint} ${c.keywords}`} onSelect={() => toggle(c.value)} className="items-start gap-3 py-2.5" aria-selected={active}>
                    <span className={cn('mt-0.5 flex size-8 shrink-0 items-center justify-center border', active ? 'border-primary bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>
                      {active ? <CheckIcon className="size-4" aria-hidden /> : <c.icon className="size-4" aria-hidden />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{c.label}</span>
                      <span className="text-muted-foreground block text-xs leading-relaxed">{c.hint}</span>
                    </span>
                    {active && value[0] === c.value ? <span className="text-primary mt-1 shrink-0 text-[10px] font-medium tracking-wide uppercase">Primary</span> : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          <div className="flex items-center justify-between gap-3 border-t px-3 py-2 text-xs">
            <span className="text-muted-foreground">{chosen.length === 0 ? 'Nothing picked yet' : chosen.map((c) => c.label).join(' · ')}</span>
            <Button
              type="button"
              size="sm"
              variant={chosen.length > 0 ? 'default' : 'outline'}
              disabled={chosen.length === 0}
              onClick={() => {
                setOpen(false);
                onDone();
              }}
            >
              Continue <ArrowRightIcon aria-hidden />
            </Button>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Three-segment progress bar with step labels. */
function Progress({ step }: { step: number }) {
  return (
    <ol className="grid grid-cols-3 gap-2" aria-label="Progress">
      {STEPS.map((s, i) => {
        const state = i < step ? 'done' : i === step ? 'current' : 'todo';
        return (
          <li key={s.key} aria-current={state === 'current' ? 'step' : undefined} className="grid gap-1.5">
            <span className={cn('h-1 transition-colors', state === 'todo' ? 'bg-border' : 'bg-primary')} aria-hidden />
            <span className={cn('text-[11px] font-medium tracking-wide uppercase', state === 'current' ? 'text-foreground' : 'text-muted-foreground')}>
              {i === 0 ? 'Topics' : i === 1 ? 'Details' : 'Contact'}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
